import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import styles from "../../styles/winterregister.module.css";
import { track } from "../../lib/mixpanel";
import { QuickConsultSheet } from "../../components/mobilebottomcta";
import { usePhoneVerify, formatPhone, PhoneVerifyTransport } from "../../components/phoneverify";
import { isPreviewMode, previewConfig } from "../winterpay/previewMock";
import { StatusBadge, BranchGrid, ProgSeg, SegPair, MiniCal, PopUp, TopBar, DoneView, IconCal } from "./sections";

/**
 * 윈터 등록 통합 페이지 suneungsunbae.com/winterregister — 상담사가 전화 상담 뒤 보내는 고정 링크 (디자인 '윈터 등록 통합' 지점 선택 화면)
 *  1단계 프리/정규 + 지점 선택 (지점별 상태 배지: 등록 가능 / 마감 임박 / 모집 마감 — 대시보드 '지점별 마감관리')
 *    - 등록 가능·마감 임박 → 팝업 '등록하러 가기' → POST /winterpay/open (그 순간부터 12시간 결제 링크) → /winterpay?id&code
 *    - 모집 마감 → CTA '정규윈터 대기신청하기' → 2단계 기본 정보(성별·학년·입실 희망일·요청) → 3단계 이름·번호 인증 → POST /winterpay/waiting → 완료
 *      대기 신청은 기존 대기 신청과 같은 waitingList 에 저장되고 같은 '대기신청 완료' 알림톡(실시간 대기현황 / 대기 취소하기 버튼)이 나간다
 *  URL: ?prog=pre|regular&branch=대치&wait=1 (결제 페이지 마감 팝업에서 넘어올 때 미리 선택)
 *  입실 희망일 범위 = 그 지점·프로그램의 유효 시작 범위(statusMap.startFrom~startTo, 결제 페이지와 동일). 일요일·오늘 이전 불가
 */

const API = "https://peetsunbae.com/dashboard/payments/winterpay";

export const BRANCHES: { ko: string; en: string }[] = [
    { ko: "대치", en: "daechi" }, { ko: "강남", en: "gangnam" }, { ko: "송파", en: "songpa" }, { ko: "분당", en: "bundang" }, { ko: "수성", en: "daegu" },
    { ko: "김포", en: "gimpo" }, { ko: "신촌", en: "sinchon" }, { ko: "강동", en: "gangdong" }, { ko: "하남", en: "hanam" }, { ko: "센텀", en: "centum" }, { ko: "동탄", en: "dongtan" },
];

export type Status = "open" | "soon" | "closed";
export type Cell = { status: Status; startFrom: string; startTo: string; fixed: boolean };
type StatusMap = { [en: string]: { [pid: string]: Cell } };
type Prog = "pre" | "regular";
const PID: { [k in Prog]: string } = { pre: "p2", regular: "p3" };
const PROG_NAME: { [k in Prog]: string } = { pre: "프리윈터", regular: "정규윈터" };

const WD = ["일", "월", "화", "수", "목", "금", "토"];
const toDate = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const md = (d: Date) => `${d.getMonth() + 1}.${d.getDate()}`;
const ymdL = (d: Date) => `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${WD[d.getDay()]})`;
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const digits = (v: string) => v.replace(/\D/g, "");

//번호 인증 — 기존 대기 신청(consultsheet)과 같은 cert API
const certTransport: PhoneVerifyTransport = {
    requestCert: (d) => {
        fetch(`https://suneungsunbae.com/api/booking/cert?telephoneNumber=${d}`, { method: "GET" }).then((r) => r.json()).catch(() => {});
        return Promise.resolve({ ok: true });
    },
    checkCert: (d, code) =>
        fetch(`https://suneungsunbae.com/api/booking/cert`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ telephoneNumber: d, certNumber: code }) })
            .then((r) => r.json()).then((res) => ({ ok: res.message === "success" })),
};

const WinterRegister: React.FC = () => {
    const wide = useMediaQuery({ query: "(min-width: 760px)" });
    const params = useMemo(() => new URLSearchParams(window.location.search), []);

    const [statusMap, setStatusMap] = useState<StatusMap | null>(null);
    const [loadErr, setLoadErr] = useState(false);
    const [prog, setProg] = useState<Prog>(params.get("prog") === "pre" ? "pre" : "regular");
    const [br, setBr] = useState<string>(() => {
        const q = (params.get("branch") || "").replace(/점$/, "");
        return BRANCHES.some(b => b.ko === q) ? q : "";
    });
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [pop, setPop] = useState<string | null>(null);
    const [quick, setQuick] = useState(false);
    const [opening, setOpening] = useState(false);
    const [openErr, setOpenErr] = useState("");
    const [date, setDate] = useState<Date | null>(null);
    const [calOpen, setCalOpen] = useState(false);
    const [view, setView] = useState<{ y: number; m: number } | null>(null);
    const [gender, setGender] = useState<"male" | "female">("male");
    const [grade, setGrade] = useState<"n" | "current">("n");
    const [note, setNote] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [duplicate, setDuplicate] = useState(false);
    const [waitErr, setWaitErr] = useState("");
    const verify = usePhoneVerify(() => "winter_register_waiting", certTransport);

    const loadStatus = useCallback(async () => {
        try {
            setLoadErr(false);
            //로컬 미리보기(localhost + ?preview=1): 결제 페이지 목 설정의 지점 상태 사용 (대치 정규만 모집 마감)
            if (isPreviewMode()) { setStatusMap((previewConfig() as any).programStatus); return; }
            const resp = await fetch(`${API}/status`);
            const result = await resp.json();
            if (result.message === "success" && result.statusMap) setStatusMap(result.statusMap);
            else setLoadErr(true);
        } catch (e) { console.log(e); setLoadErr(true); }
    }, []);

    useEffect(() => {
        loadStatus();
        track("Winter Register Page Viewed", { form_type: "winter_register", prog: params.get("prog") || "", branch: params.get("branch") || "", wait: params.get("wait") === "1" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //---- 지점·프로그램 상태
    const en = BRANCHES.find(b => b.ko === br)?.en || "";
    const cellOf = (ko: string, p: Prog = prog): Cell | null => {
        const e = BRANCHES.find(b => b.ko === ko)?.en;
        return statusMap && e && statusMap[e] && statusMap[e][PID[p]] ? statusMap[e][PID[p]] : null;
    };
    const picked = br ? cellOf(br) : null;
    const isWait = !!picked && picked.status === "closed";
    const progName = PROG_NAME[prog];

    //---- 입실 희망일 범위 (유효 시작 범위, 오늘 이전·일요일 제외). fixed 면 날짜 하나로 고정
    const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
    const range = picked ? { a: toDate(picked.startFrom), b: toDate(picked.startTo) } : null;
    const fixedDate = picked && picked.fixed ? toDate(picked.startFrom) : null;
    const dayOk = (dt: Date) => !!range && dt >= today && dt >= range.a && dt <= range.b && dt.getDay() !== 0;
    useEffect(() => {
        //지점·프로그램이 바뀌면 고정일은 자동 선택, 범위 밖 날짜는 초기화
        if (fixedDate) { setDate(fixedDate); setCalOpen(false); return; }
        if (date && !dayOk(date)) setDate(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [br, prog, statusMap]);

    const pickBranch = (ko: string) => {
        setBr(ko); setOpenErr("");
        const c = cellOf(ko);
        if (c && c.status !== "closed") setPop(ko);
    };

    //---- 등록하러 가기: 12시간 결제 링크 발급 → /winterpay
    const goPay = async () => {
        if (!pop || opening) return;
        const e = BRANCHES.find(b => b.ko === pop)?.en;
        if (!e) return;
        try {
            setOpening(true); setOpenErr("");
            track("Winter Register Go Pay", { form_type: "winter_register", branch: e, program: PID[prog] });
            const resp = await fetch(`${API}/open`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ location: e, program: PID[prog] }) });
            const result = await resp.json();
            if (result.message === "success" && result.url) { window.location.href = result.url; return; }
            if (result.message === "closed") { setOpenErr("방금 모집이 마감됐어요. 상태를 다시 불러올게요."); setPop(null); loadStatus(); return; }
            if (result.message === "tooMany") { setOpenErr("잠시 후 다시 시도해주세요."); return; }
            setOpenErr("결제 페이지를 여는 데 실패했어요. 다시 시도해주세요.");
        } catch (err) { console.log(err); setOpenErr("결제 페이지를 여는 데 실패했어요. 다시 시도해주세요."); }
        finally { setOpening(false); }
    };

    //---- 대기 신청 제출
    const submitWaiting = async () => {
        if (submitting || !en || !date || !name.trim() || !verify.verified) return;
        try {
            setSubmitting(true); setWaitErr("");
            const resp = await fetch(`${API}/waiting`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ location: en, program: prog, gender, grade, enrollmentDate: ymd(date), etc: note.trim(), name: name.trim(), telephoneNumber: digits(verify.phone), cert: verify.cert }),
            });
            const result = await resp.json();
            if (result.message === "success" || result.message === "duplicate") {
                setDuplicate(result.message === "duplicate");
                track("Winter Waiting Submitted", { form_type: "winter_register_waiting", branch: en, program: prog, duplicate: result.message === "duplicate" });
                setStep(4); window.scrollTo(0, 0);
                return;
            }
            setWaitErr("접수에 실패했어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요.");
        } catch (err) { console.log(err); setWaitErr("접수에 실패했어요. 잠시 후 다시 시도하거나 상담으로 문의해주세요."); }
        finally { setSubmitting(false); }
    };

    //---- 단계 진행
    const stepOk = step === 1 ? isWait : step === 2 ? !!date : step === 3 ? !!name.trim() && verify.verified : false;
    const ctaLabel = step === 1 ? `${progName} 대기신청하기`
        : step === 2 ? (!date ? "입실 희망일을 선택해주세요" : "다음")
        : (!name.trim() ? "이름을 입력해주세요" : !verify.verified ? "번호 인증을 완료해주세요" : submitting ? "접수 중…" : "대기 신청 완료하기");
    const onCta = () => {
        if (!stepOk || submitting) return;
        if (step === 3) { submitWaiting(); return; }
        if (step === 1) setCalOpen(!date && !fixedDate);
        setStep((step + 1) as 2 | 3);
        window.scrollTo(0, 0);
    };
    const onBack = () => {
        if (step === 1) { window.location.href = "/"; return; }
        if (step < 4) { setStep((step - 1) as 1 | 2); setCalOpen(false); window.scrollTo(0, 0); }
    };

    //---- 상태 안내 줄
    const note1 = !picked ? "" : `${br}점 ${progName}${isWait ? "는 모집이 마감됐어요." : picked.status === "soon" ? "는 곧 마감돼요." : "는 바로 등록할 수 있어요."}`;
    const ctxLine = picked ? `${br}점 · ${progName} · 대기 신청` : "";

    //---- 달력 데이터
    const calBase = range ? (range.a < today ? today : range.a) : today;
    const vy = view ? view.y : (date || calBase).getFullYear();
    const vm = view ? view.m : (date || calBase).getMonth();
    const firstM = calBase.getFullYear() * 12 + calBase.getMonth();
    const lastM = range ? range.b.getFullYear() * 12 + range.b.getMonth() : firstM;
    const curM = vy * 12 + vm;
    const cells = useMemo(() => {
        const out: { day: number | null; ok: boolean; on: boolean; today: boolean; dt?: Date }[] = [];
        const first = new Date(vy, vm, 1).getDay(), days = new Date(vy, vm + 1, 0).getDate();
        for (let i = 0; i < first; i++) out.push({ day: null, ok: false, on: false, today: false });
        for (let d = 1; d <= days; d++) {
            const dt = new Date(vy, vm, d);
            out.push({ day: d, ok: dayOk(dt), on: !!date && date.getTime() === dt.getTime(), today: dt.getTime() === today.getTime(), dt });
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vy, vm, date, br, prog, statusMap]);

    const entryHint = !picked ? "" : fixedDate ? `${br}점 ${progName}는 ${md(fixedDate)} 입실이에요` : range ? `${progName} 입실일은 ${md(range.a)} ~ ${md(range.b)} 중 고를 수 있어요 (일요일 제외)` : "";

    //---- 렌더
    if (loadErr) {
        return (
            <div className={styles.shell}>
                <div className={styles.frame}>
                    <TopBar wide={wide} step={1} onBack={() => { window.location.href = "/"; }} onQuick={() => setQuick(true)} />
                    <div className={styles.errBox}>
                        <p className={styles.errTitle}>지점 상태를 불러오지 못했어요</p>
                        <p className={styles.errSub}>잠시 후 다시 시도해주세요.</p>
                        <button type="button" className={styles.errBtn} onClick={loadStatus}>다시 시도</button>
                    </div>
                    <QuickConsultSheet open={quick} onClose={() => setQuick(false)} variant={wide ? "pc" : "mobile"} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.shell}>
            <div className={styles.frame}>
                <TopBar wide={wide} step={step} onBack={onBack} onQuick={() => setQuick(true)} />

                <div className={styles.step}>
                    {step === 1 && (
                        <>
                            <span className={styles.badgeTop}>2027 수능선배 윈터스쿨</span>
                            <h3 className={styles.q}>어느 지점으로 <b>등록할까요?</b></h3>
                            <p className={styles.qSub}>자리가 있는 지점은 바로 등록하고, 마감된 지점은 대기를 걸어둘 수 있어요.</p>
                            <ProgSeg value={prog} onChange={(p) => { setProg(p); setPop(null); setOpenErr(""); }} />
                            {!statusMap ? (
                                <div className={styles.loading}>지점 상태를 불러오는 중…</div>
                            ) : (
                                <BranchGrid branches={BRANCHES} picked={br} cellOf={(ko) => cellOf(ko)} onPick={pickBranch} />
                            )}
                            {picked && (
                                <div className={styles.note} data-tone={picked.status}>
                                    <span className={styles.noteDot} />
                                    <span>
                                        <span>{note1}</span>
                                        <span className={styles.noteLine2}>
                                            {isWait ? <><b>대기</b>를 걸어두면 자리가 날 때 가장 먼저 연락드려요.</> : picked.status === "soon" ? "지금 등록하면 자리를 바로 확보할 수 있어요." : ""}
                                        </span>
                                    </span>
                                </div>
                            )}
                            {openErr && <div className={styles.errLine}>{openErr}</div>}
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <span className={styles.ctx}>{ctxLine}</span>
                            <h3 className={styles.q}>기본 정보를 <b>알려주세요</b></h3>
                            <p className={styles.qSub}>몇 가지만 선택하면 돼요.</p>
                            <div className={styles.fields}>
                                <div className={styles.field}>
                                    <span className={styles.label}>성별</span>
                                    <SegPair items={[["male", "남학생"], ["female", "여학생"]]} value={gender} onChange={(v) => setGender(v as "male" | "female")} />
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.label}>학년</span>
                                    <SegPair items={[["n", "N수생"], ["current", "재학생"]]} value={grade} onChange={(v) => setGrade(v as "n" | "current")} />
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.label}>입실 희망일</span>
                                    <button type="button" className={styles.entryRow} data-open={calOpen ? "1" : "0"} data-fixed={fixedDate ? "1" : "0"} onClick={() => { if (!fixedDate) setCalOpen(!calOpen); }}>
                                        <span className={styles.entryVal} data-empty={date ? "0" : "1"}>{date ? ymdL(date) : "날짜 선택"}</span>
                                        <IconCal />
                                    </button>
                                    <span className={styles.entryHint}>{entryHint}</span>
                                    {calOpen && !fixedDate && (
                                        <MiniCal
                                            title={`${vy}년 ${vm + 1}월`} weekdays={WD} cells={cells}
                                            prevOff={curM <= firstM} nextOff={curM >= lastM}
                                            onPrev={() => { const v = new Date(vy, vm - 1, 1); setView({ y: v.getFullYear(), m: v.getMonth() }); }}
                                            onNext={() => { const v = new Date(vy, vm + 1, 1); setView({ y: v.getFullYear(), m: v.getMonth() }); }}
                                            onPick={(dt) => { setDate(dt); setCalOpen(false); }}
                                        />
                                    )}
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.label}>기타 요청사항 <span className={styles.opt}>(선택)</span></span>
                                    <textarea className={styles.textarea} value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} placeholder="문의하실 내용을 자유롭게 적어주세요." />
                                </div>
                            </div>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <span className={styles.ctx}>{ctxLine}</span>
                            <h3 className={styles.q}>연락처를 <b>알려주세요</b></h3>
                            <p className={styles.qSub}>자리가 나면 이 번호로 가장 먼저 연락드릴게요.</p>
                            <div className={styles.fields}>
                                <div className={styles.field}>
                                    <span className={styles.label}>이름</span>
                                    <input type="text" className={styles.input} value={name} onChange={(e) => setName(e.target.value.slice(0, 50))} placeholder="이름" />
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.label}>번호 인증</span>
                                    <div className={styles.phoneRow}>
                                        <input type="tel" inputMode="numeric" maxLength={13} className={cxs(styles.input, verify.phoneError && !verify.verified && styles.isError)} value={formatPhone(verify.phone)} onChange={(e) => verify.onPhone(e.target.value)} placeholder="010-0000-0000" readOnly={verify.verified} />
                                        <button type="button" className={styles.sendBtn} disabled={digits(verify.phone).length < 10 || verify.verified || verify.cooldown > 0} onClick={() => verify.request()}>
                                            {verify.verified ? "인증 완료" : verify.cooldown > 0 ? `다시 받기 (${verify.cooldown})` : verify.clicked ? "다시 받기" : "인증번호 받기"}
                                        </button>
                                    </div>
                                    {verify.requestError && <span className={styles.errLine}>{verify.requestError}</span>}
                                    {verify.clicked && !verify.verified && (
                                        <div className={styles.codeRow}>
                                            <input type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code" className={cxs(styles.input, verify.status === "fail" && styles.isError)} value={verify.cert} onChange={(e) => verify.onCert(e.target.value)} placeholder="인증번호" autoFocus />
                                            <span className={styles.codeStatus} data-fail={verify.status === "fail" ? "1" : "0"}>{verify.status === "fail" ? "인증번호가 맞지 않아요" : "인증번호를 보냈어요"}</span>
                                        </div>
                                    )}
                                    {verify.verified && <span className={styles.codeDone}>✓ 인증이 완료됐어요</span>}
                                </div>
                                {waitErr && <div className={styles.errLine}>{waitErr}</div>}
                            </div>
                        </>
                    )}

                    {step === 4 && (
                        <DoneView
                            title={duplicate ? "이미 대기 신청이 되어 있어요" : "대기 신청 완료!"}
                            sub={duplicate ? `${br}점 ${progName}에 같은 번호로 신청된 대기가 있어요. 자리가 나면 그 순서대로 연락드릴게요.` : `${br}점 ${progName} 대기 순서에 올렸어요. 자리가 나면 가장 먼저 연락드릴게요.`}
                            hint={duplicate ? "" : "실시간 대기현황 확인과 취소는 방금 보내드린 알림톡 버튼에서 할 수 있어요."}
                            onConfirm={() => { window.location.href = "/"; }}
                        />
                    )}
                </div>

                {step < 4 && (
                    <div className={styles.ctaBar}>
                        <button type="button" className={styles.cta} disabled={!stepOk || submitting} onClick={onCta}>{ctaLabel}</button>
                    </div>
                )}

                {pop && step === 1 && (() => {
                    const c = cellOf(pop);
                    if (!c) return null;
                    return (
                        <PopUp
                            badge={<StatusBadge status={c.status} />}
                            place={`${pop}점 ${progName}는`}
                            head={c.status === "soon" ? "곧 마감돼요" : "바로 등록할 수 있어요"}
                            loading={opening}
                            err={openErr}
                            onGo={goPay}
                            onClose={() => { if (!opening) { setPop(null); setOpenErr(""); } }}
                        />
                    );
                })()}

                <QuickConsultSheet open={quick} onClose={() => setQuick(false)} variant={wide ? "pc" : "mobile"} />
            </div>
        </div>
    );
};

const cxs = (...a: (string | false | undefined | null)[]) => a.filter(Boolean).join(" ");

export default WinterRegister;
